import React from 'react';

import { PageProps } from '@fastlessjs/backoffice-layer/dist/components/pages/types';
import { Listing } from '@fastlessjs/backoffice-layer/dist/components/templates';
import { Enterprise, useMultiTenancy } from '@fastlessjs/backoffice-layer/dist/hooks';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { useQueryClient } from '@tanstack/react-query';
import { Space } from 'antd';
import { navigate } from 'hookrouter-react';

import { OrderReturnModal } from './OrderReturnModal';
import SearchOrderModal from './SearchOrderModal';
import EurekaRefreshButton from '../../components/EurekaRefreshButton';
import { t } from '../../config/translations';
import { useOrders } from '../hooks';
import { orderListSchema } from '../schemas';
import { Order } from '../types';

// Register this component for debug tracking
debug.registerComponent('OrdersTable');

const Table = ({ org }: PageProps) => {
  debug.trackRender('OrdersTable');
  debug.log('OrdersTable', `Rendering with org: ${org}`);
  const queryClient = useQueryClient();
  const { setPage, getPage } = useMultiTenancy();
  const enterpriseId = getPage('enterprises')?.[org]?.enterpriseId;
  const enterpriseName = getPage('enterprises')?.[org]?.label;
  const enterpriseIdNum =
    enterpriseId !== undefined && enterpriseId !== null ? Number(enterpriseId) : NaN;
  const canRefreshFromEureka = Number.isFinite(enterpriseIdNum);

  const handleSubmit = async (orderId: string, clientId?: string) => {
    debug.log('OrdersTable', `Navigating to order: ${orderId} for client: ${clientId}`);
    const eLabel = await queryClient
      .getQueryData<any>(['enterprises', 'select', org])
      .pages[0].data.find((e: Enterprise) => e.enterpriseId.toString() === clientId)
      .enterpriseName;
    await setPage({ [org]: { enterpriseId: clientId, label: eLabel } }, 'enterprises');
    navigate(`/${org}/orders/${orderId}/view`);
  };

  return (
    <Listing
      title={t('page.orders')}
      name="orders"
      subTitle={`${enterpriseName} - ${enterpriseId}`}
      idKey="ndoc"
      org={org!}
      schema={orderListSchema}
      options={{
        clientId: enterpriseId,
        tenantId: enterpriseId,
      }}
      fetchHook={useOrders as any}
      enableSelection={false}
      hasNew={false}
      hasEdit={false}
      // eslint-disable-next-line react/no-unstable-nested-components
      ExtraButtons={() => {
        return (
          <Space size="small" style={{ display: 'flex' }}>
            <OrderReturnModal handleSubmit={handleSubmit} />
            <SearchOrderModal org={org} />
            <EurekaRefreshButton
              entity="order"
              filter={{ enterpriseId: enterpriseIdNum }}
              queryKeysToInvalidate={[['orders', String(enterpriseId)]]}
              disabled={!canRefreshFromEureka}
            />
          </Space>
        );
      }}
      // eslint-disable-next-line react/no-unstable-nested-components
      ExtraActions={(props: any) => {
        const order = props?.record as Order | undefined;
        const orderId = order?.ndoc !== undefined ? String(order.ndoc) : '';
        return (
          <EurekaRefreshButton
            mode="row"
            entity="order"
            items={[{ orderId, enterpriseId: enterpriseIdNum }]}
            queryKeysToInvalidate={[
              ['orders', String(enterpriseId)],
              ['orders', orderId, enterpriseId],
            ]}
            disabled={!canRefreshFromEureka || orderId.length === 0}
          />
        );
      }}
    />
  );
};

export default Table;

// Mark this component as initialized
debug.markInitialized('OrdersTable');
